import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageUploader } from "@/components/v2/ImageUploader";
import { useToast } from "@/components/v2/Toast";
import { useTenantMe } from "@/lib/tenant-me";
import {
  useUpdateUserMe,
  useUploadUserAvatar,
  useUserMe,
} from "@/lib/user-me";
import { StepHero } from "./_StepHero";

/**
 * Tier-2 mini-onboarding: shown the first time an invited user lands
 * on the operator dashboard with no first name on their profile. No
 * sidebar rail (no journey to show — it's just one step), but the
 * styling matches the new Notion-like field treatment so the experience
 * feels consistent with the full wizard.
 *
 * Gate logic that routes here lives in `App.tsx`'s
 * `RequireOnboardingDone`.
 */
export function WelcomePersonalStep() {
  const me = useUserMe();
  const tenant = useTenantMe();
  const update = useUpdateUserMe();
  const uploadAvatar = useUploadUserAvatar();
  const navigate = useNavigate();
  const toast = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (me.data) {
      setFirstName(me.data.firstName ?? "");
      setLastName(me.data.lastName ?? "");
    }
  }, [me.data]);

  const trimmed = firstName.trim();
  const disabled = trimmed.length === 0 || submitting;

  const onSubmit = async () => {
    if (disabled) return;
    setSubmitting(true);
    try {
      await update.mutateAsync({
        firstName: trimmed,
        lastName: lastName.trim() || undefined,
      });
      navigate("/", { replace: true });
    } catch (e) {
      toast.error("Couldn't save", (e as Error).message);
      setSubmitting(false);
    }
  };

  const tenantName = tenant.data?.tenant?.name ?? "Booqly";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Minimal top bar — matches the shell's mobile bar */}
      <div className="border-b border-border bg-background px-5 h-14 flex items-center">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-bold">
            B
          </div>
          <span className="text-sm font-semibold tracking-tight">Booqly</span>
        </div>
      </div>

      <main className="flex-1 flex items-start justify-center px-6 md:px-10 py-12 md:py-20">
        <div className="w-full max-w-[480px] space-y-10">
          <StepHero
            eyebrow={`Welcome to ${tenantName}`}
            title="Tell us about yourself"
            subtitle="Your teammates will see your name on bookings and team activity. You can update this any time from Account settings."
          />

          <div className="space-y-8">
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Profile photo</Label>
              <ImageUploader
                shape="circle"
                size={80}
                label="Add a photo"
                value={me.data?.avatarUrl}
                onUpload={(file) => uploadAvatar.mutateAsync(file)}
                helpText="Optional. PNG or JPG, up to 5 MB."
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName" className="text-[13px] font-medium">
                  First name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="firstName"
                  placeholder="Jane"
                  autoFocus
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !disabled) onSubmit();
                  }}
                  className="h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName" className="text-[13px] font-medium">
                  Last name
                </Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !disabled) onSubmit();
                  }}
                  className="h-11"
                />
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-border flex justify-end">
            <Button onClick={onSubmit} disabled={disabled} size="lg">
              {submitting ? "Saving…" : "Continue"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
